import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, timer, switchMap, catchError, of } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface DexScreenerPair {
  chainId: string;
  dexId: string;
  url: string;
  pairAddress: string;
  labels: string[];
  baseToken: {
    address: string;
    name: string;
    symbol: string;
  };
  quoteToken: {
    address: string;
    name: string;
    symbol: string;
  };
  priceNative: string;
  priceUsd: string;
  txns: {
    m5: { buys: number; sells: number };
    h1: { buys: number; sells: number };
    h6: { buys: number; sells: number };
    h24: { buys: number; sells: number };
  };
  volume: {
    h24: number;
    h6: number;
    h1: number;
    m5: number;
  };
  priceChange: {
    h6?: number;
    h24?: number;
  };
  liquidity: {
    usd: number;
    base: number;
    quote: number;
  };
  fdv: number;
  marketCap: number;
  pairCreatedAt: number;
  info: {
    imageUrl: string;
    header: string;
    openGraph: string;
    websites: Array<{ label: string; url: string }>;
    socials: Array<{ type: string; url: string }>;
  };
}

export interface DexScreenerResponse {
  schemaVersion: string;
  pairs: DexScreenerPair[];
}

export interface MarketData {
  priceUsd: number;
  priceChange24h: number;
  marketCap: number;
  volume24h: number;
  liquidity: number;
  fdv: number;
  symbol: string;
  name: string;
  ethPriceUsd?: number; // ETH price in USD from the pair data
}

@Injectable({
  providedIn: 'root'
})
export class DexScreenerService {
  private readonly API_BASE_URL = 'https://api.dexscreener.com/latest/dex/tokens';
  private marketDataSubject = new BehaviorSubject<MarketData | null>(null);
  public marketData$ = this.marketDataSubject.asObservable();

  constructor(private http: HttpClient) {
    // Auto-refresh market data every 30 seconds
    this.startAutoRefresh();
  }

  /**
   * Fetch market data for the configured token
   */
  fetchMarketData(): Observable<MarketData | null> {
    if (!environment.dexscreenerTokenAddress) {
      console.warn('DexScreener token address not configured');
      return of(null);
    }

    const url = `${this.API_BASE_URL}/${environment.dexscreenerTokenAddress}`;
    
    return this.http.get<DexScreenerResponse>(url).pipe(
      switchMap(response => {
        if (response.pairs && response.pairs.length > 0) {
          // Use the first pair (usually the most liquid one)
          const pair = response.pairs[0];
          
          // Calculate ETH price in USD from the pair data
          let ethPriceUsd: number | undefined;
          if (pair.quoteToken.symbol === 'ETH' && pair.priceNative) {
            // If the quote token is ETH, we can calculate ETH price from the pair
            // priceNative is the price of base token in ETH
            // priceUsd is the price of base token in USD
            // So ETH price = priceUsd / priceNative
            ethPriceUsd = parseFloat(pair.priceUsd) / parseFloat(pair.priceNative);
          }
          
          const marketData: MarketData = {
            priceUsd: parseFloat(pair.priceUsd),
            priceChange24h: pair.priceChange.h24 || 0,
            marketCap: pair.marketCap,
            volume24h: pair.volume.h24,
            liquidity: pair.liquidity.usd,
            fdv: pair.fdv,
            symbol: pair.baseToken.symbol,
            name: pair.baseToken.name,
            ethPriceUsd: ethPriceUsd
          };
          
          this.marketDataSubject.next(marketData);
          return of(marketData);
        } else {
          console.warn('No pairs found for token:', environment.dexscreenerTokenAddress);
          return of(null);
        }
      }),
      catchError(error => {
        console.error('Error fetching DexScreener data:', error);
        return of(null);
      })
    );
  }

  /**
   * Get current market data (cached)
   */
  getCurrentMarketData(): MarketData | null {
    return this.marketDataSubject.value;
  }

  /**
   * Start auto-refresh of market data
   */
  private startAutoRefresh(): void {
    // Initial fetch
    this.fetchMarketData().subscribe();
    
    // Refresh every 30 seconds
    timer(30000, 30000).pipe(
      switchMap(() => this.fetchMarketData())
    ).subscribe();
  }

  /**
   * Format price for display
   */
  formatPrice(price: number): string {
    if (price < 0.000001) {
      return `$${price.toExponential(2)}`;
    } else if (price < 0.01) {
      return `$${price.toFixed(6)}`;
    } else if (price < 1) {
      return `$${price.toFixed(4)}`;
    } else {
      return `$${price.toFixed(2)}`;
    }
  }

  /**
   * Format market cap for display
   */
  formatMarketCap(marketCap: number): string {
    if (marketCap >= 1e9) {
      return `$${(marketCap / 1e9).toFixed(2)}B`;
    } else if (marketCap >= 1e6) {
      return `$${(marketCap / 1e6).toFixed(2)}M`;
    } else if (marketCap >= 1e3) {
      return `$${(marketCap / 1e3).toFixed(2)}K`;
    } else {
      return `$${marketCap.toFixed(2)}`;
    }
  }

  /**
   * Format volume for display
   */
  formatVolume(volume: number): string {
    if (volume >= 1e9) {
      return `$${(volume / 1e9).toFixed(2)}B`;
    } else if (volume >= 1e6) {
      return `$${(volume / 1e6).toFixed(2)}M`;
    } else if (volume >= 1e3) {
      return `$${(volume / 1e3).toFixed(2)}K`;
    } else {
      return `$${volume.toFixed(2)}`;
    }
  }

  /**
   * Get price change class for styling
   */
  getPriceChangeClass(change: number): string {
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'neutral';
  }

  /**
   * Format ETH price for display
   */
  formatEthPrice(ethPrice: number): string {
    if (ethPrice >= 1000) {
      return `$${ethPrice.toFixed(0)}`;
    } else if (ethPrice >= 100) {
      return `$${ethPrice.toFixed(1)}`;
    } else {
      return `$${ethPrice.toFixed(2)}`;
    }
  }
}
